// Which deployment an org's data lives behind.
//
// `prod.api.tryrubicon.com` and `us.api.tryrubicon.com` are separate
// deployments in separate AWS partitions with separate databases, so an org row
// exists in exactly one of them. Anything the portal does *before* it knows a
// user's region — the pre-auth org picker and its SSO discovery — therefore has
// to ask every region, not just the one the client happens to be pointed at.
import { APP_OR_SERVER_URL } from "@/environment";

export type ApiRegion = "ca" | "us";

/** Default first, so single-region environments collapse onto it. */
export const API_REGIONS: readonly ApiRegion[] = ["ca", "us"];

export const DEFAULT_API_REGION: ApiRegion = "ca";

/**
 * Where a dev/admin session's region override lives. Session-scoped so it dies
 * with the tab rather than following the user into their next sign-in.
 */
export const API_REGION_STORAGE_KEY = "apiRegion";

const REGION_HOSTS: Record<ApiRegion, string> = {
  ca: "prod.api.tryrubicon.com",
  us: "us.api.tryrubicon.com",
};

export function isApiRegion(value: unknown): value is ApiRegion {
  return API_REGIONS.includes(value as ApiRegion);
}

/**
 * `baseUrl` re-pointed at `region`'s host. A no-op for local and test base URLs,
 * which are single-region.
 */
export function baseUrlForRegion(baseUrl: string, region: ApiRegion): string {
  const target = REGION_HOSTS[region];
  return Object.values(REGION_HOSTS).reduce(
    (url, host) => url.replace(host, target),
    baseUrl,
  );
}

export interface DiscoveryTarget {
  region: ApiRegion;
  baseUrl: string;
}

/**
 * Every deployment the pre-auth directory has to ask — one per region.
 * Single-region environments (local, test) collapse to one target, since their
 * base URL names neither production host.
 */
export function discoveryTargets(): DiscoveryTarget[] {
  if (!APP_OR_SERVER_URL) {
    console.error("discoveryTargets: VITE_OR_SERVER_URL is not set");
    return [];
  }
  const targets: DiscoveryTarget[] = [];
  for (const region of API_REGIONS) {
    const regionBaseUrl = baseUrlForRegion(APP_OR_SERVER_URL, region);
    if (!targets.some((target) => target.baseUrl === regionBaseUrl)) {
      targets.push({ baseUrl: regionBaseUrl, region });
    }
  }
  return targets;
}

/** Bounds a region that accepts the connection and then hangs. */
export const DISCOVERY_TIMEOUT_MS = 8000;
